/**
 * schedule-links.ts — the rules behind "what will we have open on the day".
 *
 * A schedule entry can name a board, an evaluation or a game
 * (migrations/0064_schedule_entry_links.sql). The database holds that as three
 * nullable foreign keys with exactly one set, which is the right shape for a
 * row and the wrong shape for rendering, so everything that turns one into a
 * kind, a title and a destination lives here.
 *
 * Pure — no React, no supabase — so `scripts/today-check.mjs` can assert it
 * without a browser, the same arrangement as lib/lobby.ts and lib/board-select.ts.
 */
import { lobbyKind } from '@/lib/lobby'
import type { Game, ScheduleEntryLinkKind, ScheduleEntryLinkWithTarget } from '@/lib/types'

/** In the order the pickers offer them, and the order they render in. */
export const LINK_KINDS: ScheduleEntryLinkKind[] = ['board', 'evaluation', 'game']

export const LINK_KIND_LABEL: Record<ScheduleEntryLinkKind, string> = {
  board: 'Board',
  evaluation: 'Evaluation',
  game: 'Game',
}

/** Which of the three columns this row actually carries. */
export function linkKindOf(link: ScheduleEntryLinkWithTarget): ScheduleEntryLinkKind | null {
  if (link.board_id) return 'board'
  if (link.evaluation_id) return 'evaluation'
  if (link.game_id) return 'game'
  // Unreachable through the API — the check constraint refuses a row with no
  // target — but a null-everything row would otherwise render as a blank pill
  // that goes nowhere, so callers are given something to skip.
  return null
}

/** The id of the thing linked, whichever kind it is. */
export function linkTargetId(link: ScheduleEntryLinkWithTarget): string | null {
  return link.board_id ?? link.evaluation_id ?? link.game_id ?? null
}

/**
 * What to call it.
 *
 * Always the target's LIVE name: these rows cascade with their target (0064),
 * so unlike a schedule entry's topic there is no stale copy to prefer or fall
 * back to. An embed that came back null means the caller selected without it.
 */
export function linkTitle(link: ScheduleEntryLinkWithTarget): string {
  return link.boards?.title ?? link.evaluations?.name ?? link.games?.title ?? 'Untitled'
}

/**
 * Where a game link sends you.
 *
 * A solo game is its own page and can simply be opened (0060, 0062). Everything
 * else needs a session or a lobby that the ROOM starts together — there is
 * nothing to link to until someone presses the button — so those land on Games,
 * where starting and joining live.
 *
 * The solo test is the one games.tsx uses, lobby check first: a lobby game may
 * be started by its host alone, so its min_players is 1 too and the plain
 * `min_players <= 1` test would send the team to a page that does not exist.
 */
export function isSoloGame(game: Pick<Game, 'slug' | 'min_players'>): boolean {
  return !lobbyKind(game.slug) && game.min_players <= 1
}

export function gamePath(game: Pick<Game, 'slug' | 'min_players'>): string {
  return isSoloGame(game) ? `/games/${game.slug}` : '/games'
}

/** The route this link opens, or null if there is nothing to open. */
export function linkPath(link: ScheduleEntryLinkWithTarget): string | null {
  if (link.board_id) return `/boards/${link.board_id}`
  if (link.evaluation_id) return `/evaluations/${link.evaluation_id}`
  if (link.games) return gamePath(link.games)
  // A game link selected without its embed: the catalog row is what knows
  // whether the game is solo, so without it the honest answer is the list.
  if (link.game_id) return '/games'
  return null
}

/** The insert body for a new link. `entry_id` decides the team; see 0064. */
export function linkRow(
  entryId: string,
  kind: ScheduleEntryLinkKind,
  targetId: string,
  displayOrder: number,
): Record<string, unknown> {
  return {
    entry_id: entryId,
    board_id: kind === 'board' ? targetId : null,
    evaluation_id: kind === 'evaluation' ? targetId : null,
    game_id: kind === 'game' ? targetId : null,
    display_order: displayOrder,
  }
}

/**
 * The ids already linked to this entry, by kind — what a picker must not offer
 * a second time. A partial unique index refuses the duplicate anyway (0064);
 * this is what keeps the lead from meeting it.
 */
export function linkedIds(
  links: ScheduleEntryLinkWithTarget[],
  kind: ScheduleEntryLinkKind,
): Set<string> {
  const ids = new Set<string>()
  for (const l of links) {
    if (linkKindOf(l) === kind) {
      const id = linkTargetId(l)
      if (id) ids.add(id)
    }
  }
  return ids
}

/**
 * Display order, then insertion order.
 *
 * The query asks for this too; doing it again here is what keeps a locally
 * added link in the right place before the refetch lands.
 */
export function sortLinks<T extends { display_order: number; created_at: string }>(links: T[]): T[] {
  return [...links].sort(
    (a, b) => a.display_order - b.display_order || a.created_at.localeCompare(b.created_at),
  )
}
